import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigation } from "../contexts/NavigationContext";
import { Loader } from "lucide-react";
import { APP_NAME } from "../constants/app";

// Shared promise to prevent duplicate GUID exchange when React Strict Mode double-mounts
const guidExchangePromises = new Map<string, Promise<Response>>();

export const SSOCallback: React.FC = () => {
  const { refreshUser } = useAuth();
  const { navigate } = useNavigation();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    if (!hasProcessedRef.current) {
      hasProcessedRef.current = true;
      handleSSOCallback();
    }
  }, []);

  const handleSSOCallback = async () => {
    try {
      let isLoggingOut = false;
      try {
        isLoggingOut = sessionStorage.getItem("isLoggingOut") === "true";
        if (isLoggingOut) {
          console.log("[SSO Callback] Clearing logout flag from previous session");
          sessionStorage.removeItem("isLoggingOut");
        }
      } catch (storageError) {
        console.warn("[SSO Callback] Could not access sessionStorage:", storageError);
      }

      const urlParams = new URLSearchParams(window.location.search);
      const guid = urlParams.get("guid");
      const state = urlParams.get("state");

      console.log("[SSO Callback] Processing GUID:", guid ? guid.substring(0, 8) + "..." : "none");

      if (!guid) {
        console.error("[SSO Callback] Missing GUID parameter");
        setError("Missing authentication token. Please try again.");
        setIsLoading(false);
        return;
      }

      // Check if this GUID has already been fully processed and token stored
      const processedGuid = sessionStorage.getItem("processedGuid");
      if (processedGuid === guid) {
        console.log("[SSO Callback] GUID already processed, checking for stored token");
        const existingToken = localStorage.getItem("token");
        if (existingToken) {
          await refreshUser();
          navigate("/");
          return;
        }
      }

      try {
        sessionStorage.setItem("processingGuid", guid);
      } catch (storageError) {
        console.warn("[SSO Callback] Could not mark GUID as processing:", storageError);
      }

      // Deduplicate: React Strict Mode double-mounts in dev, causing two exchange requests.
      // GUIDs are single-use - the second request would fail with "already been used".
      let exchangePromise = guidExchangePromises.get(guid);
      if (!exchangePromise) {
        exchangePromise = fetch(`${apiUrl}/api/sso/callback`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ guid, state }),
        });
        guidExchangePromises.set(guid, exchangePromise);
        exchangePromise.finally(() => guidExchangePromises.delete(guid));
      }
      const response = await exchangePromise;

      const data = await response.json();

      console.log("[SSO Callback] Response:", {
        status: response.status,
        success: data.success,
        hasUser: !!data.user,
      });

      if (!data.success) {
        throw new Error(data.error || "Authentication failed");
      }

      try {
        sessionStorage.setItem("processedGuid", guid);
        sessionStorage.removeItem("processingGuid");
      } catch (storageError) {
        console.warn("[SSO Callback] Could not mark GUID as processed:", storageError);
      }

      try {
        localStorage.setItem("token", data.token);
        localStorage.setItem("user", JSON.stringify(data.user));
      } catch (storageError) {
        console.error("[SSO Callback] Failed to store token in localStorage:", storageError);
        setError("Browser storage is blocked. Please check your privacy settings and allow storage for this site.");
        setIsLoading(false);
        return;
      }

      if (data.needsAccountSetup) {
        console.log("[SSO Callback] User needs account setup, redirecting to onboarding");
        navigate("/auth/onboarding");
        return;
      }

      await refreshUser();
      navigate("/");
    } catch (err: any) {
      console.error("SSO callback error:", err);
      setError(err.message || "Authentication failed. Please try again.");
      setIsLoading(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="mb-6">
            <img src="/logo.svg" alt={APP_NAME} className="w-20 h-20 mx-auto object-contain" />
          </div>
          <div className="text-error-600 mb-4">
            <svg
              className="mx-auto h-12 w-12"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Authentication Error
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate("/auth/login")}
            className="w-full bg-accent-600 text-white py-2 px-4 rounded-lg hover:bg-accent-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="mb-6">
          <img src="/logo.svg" alt={APP_NAME} className="w-20 h-20 mx-auto object-contain" />
        </div>
        <Loader className="animate-spin h-12 w-12 text-accent-600 mx-auto mb-4" />
        <p className="text-gray-600 text-lg">Completing sign in...</p>
      </div>
    </div>
  );
};
